import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getMetadata
} from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { storage } from './firebase';
import { FileUploadType } from '../types';

// 파일 크기 제한 (바이트)
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10MB

// 허용된 이미지 MIME 타입
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// 허용된 PDF MIME 타입
const ALLOWED_PDF_TYPES = ['application/pdf'];

// 파일 검증
const validateFile = (file: File, type: FileUploadType): void => {
  if (type === 'photo') {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('이미지 파일만 업로드 가능합니다. (JPEG, PNG, WebP)');
    }
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('이미지 파일 크기는 5MB를 초과할 수 없습니다.');
    }
  } else {
    if (!ALLOWED_PDF_TYPES.includes(file.type)) {
      throw new Error('PDF 파일만 업로드 가능합니다.');
    }
    if (file.size > MAX_PDF_SIZE) {
      throw new Error('PDF 파일 크기는 10MB를 초과할 수 없습니다.');
    }
  }
};

// 파일명 생성
const generateFileName = (originalName: string, userId: string, type: FileUploadType): string => {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  const sanitizedName = originalName
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .substring(0, 50);
  
  return `${type}_${userId}_${timestamp}_${sanitizedName}`;
};

// 저장 경로 생성
const getStoragePath = (fileName: string, type: FileUploadType): string => {
  if (type === 'photo') {
    return `photos/${fileName}`;
  } else {
    return `certificates/${fileName}`;
  }
};

// 파일 업로드
export const uploadFile = async (
  file: File,
  userId: string,
  type: FileUploadType,
  onProgress?: (progress: number) => void
): Promise<{
  url: string;
  fileName: string;
  uploadedAt: Timestamp;
}> => {
  try {
    // 파일 검증
    validateFile(file, type);

    // 파일명 생성
    const fileName = generateFileName(file.name, userId, type);
    const storagePath = getStoragePath(fileName, type);

    // Storage 참조 생성
    const storageRef = ref(storage, storagePath);

    // 파일 업로드
    const snapshot = await uploadBytes(storageRef, file);

    // 다운로드 URL 가져오기
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      fileName: fileName,
      uploadedAt: Timestamp.now()
    };
  } catch (error: any) {
    console.error('파일 업로드 실패:', error);
    throw new Error(`파일 업로드 실패: ${error.message}`);
  }
};

// 파일 삭제
export const deleteFile = async (fileName: string, type: FileUploadType): Promise<void> => {
  try {
    const storagePath = getStoragePath(fileName, type);
    const storageRef = ref(storage, storagePath);
    
    await deleteObject(storageRef);
  } catch (error: any) {
    console.error('파일 삭제 실패:', error);
    // 파일이 존재하지 않는 경우는 에러로 처리하지 않음
    if (error.code !== 'storage/object-not-found') {
      throw new Error(`파일 삭제 실패: ${error.message}`);
    }
  }
};

// 파일 정보 가져오기
export const getFileInfo = async (fileName: string, type: FileUploadType): Promise<{
  size: number;
  contentType: string;
  timeCreated: string;
} | null> => {
  try {
    const storagePath = getStoragePath(fileName, type);
    const storageRef = ref(storage, storagePath);
    
    const metadata = await getMetadata(storageRef);
    
    return {
      size: metadata.size,
      contentType: metadata.contentType || '',
      timeCreated: metadata.timeCreated
    };
  } catch (error: any) {
    console.error('파일 정보 조회 실패:', error);
    return null;
  }
};

// 이미지 리사이즈 (클라이언트 사이드)
export const resizeImage = (
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 600,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // 비율 계산
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
      
      if (ratio >= 1) {
        // 리사이즈가 필요 없음
        resolve(file);
        return;
      }

      // 캔버스 크기 설정
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      // 이미지 그리기
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Blob으로 변환
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            reject(new Error('이미지 리사이즈 실패'));
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('이미지 로드 실패'));
    };

    img.src = URL.createObjectURL(file);
  });
};

// 썸네일 생성
export const createThumbnail = (
  file: File,
  size: number = 150
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // 정사각형 썸네일
      canvas.width = size;
      canvas.height = size;

      // 이미지의 중앙 부분을 정사각형으로 자르기
      const sourceSize = Math.min(img.width, img.height);
      const sourceX = (img.width - sourceSize) / 2;
      const sourceY = (img.height - sourceSize) / 2;

      ctx?.drawImage(
        img,
        sourceX, sourceY, sourceSize, sourceSize,
        0, 0, size, size
      );

      // Data URL로 변환
      const dataURL = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataURL);
    };

    img.onerror = () => {
      reject(new Error('썸네일 생성 실패'));
    };

    img.src = URL.createObjectURL(file);
  });
};

// 파일 타입 확인
export const getFileType = (fileName: string): FileUploadType | null => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'webp'].includes(extension || '')) {
    return 'photo';
  } else if (extension === 'pdf') {
    return 'initialCert'; // 기본값, 실제 사용 시 구분 필요
  }
  
  return null;
};

// 파일 크기 포맷팅
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
